import {
  AssessmentSettingsFragment,
  MarkingTool,
  RequirementsFragment,
  TaskKind,
} from "@/generated/graphql";

/**
 * Convert Assessment Settings back into editable Task requirements.
 */
export function fromAssessmentSettings(
  settings: AssessmentSettingsFragment
): RequirementsFragment {
  const {
    id,
    dueDate,
    maxGrade,
    draftDueDate,
    wordLimit,
    timeLimit,
    weight,
    referencingStyle,
    countReferences,
    countFootnotes,
    anonymousMarking,
    assessmentType,
    examEndDate,
    examReadingTime,
    examStartDate,
    examTiming,
    examType,
    examWritingTime,
    lateSubmissionTimeLimit,
    allowGenerativeAi,
    markingToolPdf,
    restrictMarkingToolPdf,
    rubricDisplayDisabled,
    submitAttemptLimit,
  } = settings;

  return {
    id,
    assessmentType,
    dueDate,
    returnDate: settings.feedbackDate,

    draftDueDate,
    noDraft: !settings.draftEnabled,
    individualDraftFeedbackAlwaysAvailable:
      settings.individualDraftFeedbackAlwaysAvailable ?? false,
    draftScoringEnabled: settings.draftScoringEnabled ?? true,

    maxGrade,
    anonymousMarking,
    gradingService: markingTooltoString(settings.markingTool),

    sViewReports: settings.tiiSViewReports,
    draftSViewReports: settings.tiiDraftSViewReports,

    lateSubmissionTimeLimit,
    allowLateResubmission: settings.lateResubmissionEnabled,

    weight,
    noWeight: settings.weightEnabled === false || weight === null,

    wordLimit,
    noLimit: settings.wordLimitEnabled === false || wordLimit === null,

    timeLimit,
    noTimeLimit: settings.timeLimitEnabled === false || timeLimit === null,
    submitAttemptLimit,

    countReferences: countReferences ?? false,
    countFootnotes: countFootnotes ?? false,

    referencingStyle,
    noReferencing:
      settings.referencingStyleEnabled === false ||
      referencingStyle === null ||
      referencingStyle === "",

    hasChanged: true,

    enableExamAutoSubmission: settings.examAutoSubmissionDisabled === false,
    enableSubmissionPdf: settings.submissionPdfDisabled === false,
    examEndDate,
    examReadingTime,
    noExamReadingTime:
      settings.examReadingTimeDisabled === true || examReadingTime === null,
    examStartDate,
    examTiming,
    examType,
    examWritingTime,
    disableExamBacktrack: settings.examBacktrackDisabled,

    examFeedbackDate: settings.feedbackDate,
    noExamFeedbackDate:
      settings.feedbackDateDisabled === true || settings.feedbackDate === null,

    enableExamLdb: settings.examLdbEnabled,
    enableExamAccessCode: settings.examAccessCodeEnabled,

    restrictMarkingToolPdf,
    markingToolPdf,
    rubricDisplayDisabled,

    allowGenerativeAi,
    disableSpellCheck: settings.spellCheckDisabled,
    enableExamReentryPrevention: settings.examReentryPreventionEnabled,
    examInactivityTimeout: settings.examInactivityTimeout,
    examSchoolyearEnabled: settings.examSchoolyearEnabled,
  };
}

/**
 * For Viva tasks, derive requirements from AssessmentSettings (the source of
 * truth) instead of the legacy task requirements. All other task kinds keep
 * their existing requirements unchanged.
 */
export function resolveTaskRequirements(
  task: { kind: TaskKind; requirements: RequirementsFragment },
  settings: AssessmentSettingsFragment | null
): RequirementsFragment {
  if (task.kind === TaskKind.Viva && settings) {
    return fromAssessmentSettings(settings);
  }
  return task.requirements;
}

function markingTooltoString(markingTool: MarkingTool | null) {
  switch (markingTool) {
    case MarkingTool.Turnitin:
      return "turnitin";
    case MarkingTool.Cadmusgrader:
      return "cadmusgrader";
    case MarkingTool.Moodlegrader:
      return "moodlegrader";
    case MarkingTool.Speedgrader:
      return "speedgrader";
    default:
      return "unknown";
  }
}
